import { type MouseEvent, type ReactNode, useState } from "react";

import * as Collapsible from "@radix-ui/react-collapsible";
import { cx } from "class-variance-authority";

import { SidebarCaret } from "./icons/SidebarCaret";
import { SidebarMenuItem } from "./SidebarMenuItem";
import * as theme from "./SidebarNavGroup.css";

export interface SidebarNavGroupProps {
    label: string;
    icon?: ReactNode;
    /** Starting state. Ignored once `expanded` is supplied. */
    defaultExpanded?: boolean;
    expanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    href?: string;
    onHeaderClick?: (event: MouseEvent<HTMLElement>) => void;
    /** Children stay mounted and are closed by CSS, so the icon never re-renders. */
    collapsed?: boolean;
    /** Holds the page currently open. Highlights the header only while `collapsed` hides the children. */
    active?: boolean;
    caretLabel?: string;
    children: ReactNode;
    className?: string;
}

export function SidebarNavGroup({
    label,
    icon,
    defaultExpanded = true,
    expanded,
    onExpandedChange,
    href,
    onHeaderClick,
    collapsed = false,
    active = false,
    caretLabel,
    children,
    className,
}: SidebarNavGroupProps) {
    const [uncontrolledExpanded, setUncontrolledExpanded] =
        useState(defaultExpanded);
    // The collapsed sidebar shuts every group, so Radix is driven rather than asked.
    const isExpanded = (expanded ?? uncontrolledExpanded) && !collapsed;

    function handleOpenChange(open: boolean) {
        if (expanded === undefined) {
            setUncontrolledExpanded(open);
        }
        onExpandedChange?.(open);
    }

    return (
        <Collapsible.Root
            open={isExpanded}
            onOpenChange={handleOpenChange}
            data-slot="sidebar-nav-group"
            className={cx(theme.root, className)}
        >
            <div
                className={cx(theme.header, collapsed && theme.collapsedHeader)}
            >
                <SidebarMenuItem
                    label={label}
                    icon={icon}
                    type={collapsed ? "iconOnly" : "noCaret"}
                    href={href}
                    onClick={onHeaderClick}
                    active={collapsed && active}
                    className={collapsed ? undefined : theme.reservesCaret}
                />
                {/* A sibling of the row, not a child: a button inside an anchor is invalid. */}
                <Collapsible.Trigger
                    className={cx(
                        theme.caretButton,
                        collapsed && theme.collapsedCaretButton
                    )}
                    aria-label={caretLabel ?? label}
                    inert={collapsed}
                    aria-hidden={collapsed}
                >
                    <SidebarCaret expanded={isExpanded} />
                </Collapsible.Trigger>
            </div>
            <Collapsible.Content
                forceMount
                // `inert` blocks pointer and focus; `aria-hidden` covers readers
                // that do not honour it yet.
                inert={!isExpanded}
                aria-hidden={!isExpanded}
            >
                {/* Radix zeroes the transition on its own node to measure it, so
                    the open/close animation has to live one level in. */}
                <div className={theme.slot}>
                    <div className={theme.slotInner}>
                        <div
                            className={cx(
                                theme.children,
                                collapsed && theme.collapsedChildren
                            )}
                        >
                            <span className={theme.connector} aria-hidden />
                            {children}
                        </div>
                    </div>
                </div>
            </Collapsible.Content>
        </Collapsible.Root>
    );
}
